import React, { useEffect, useState } from 'react';
import {
  Button,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import { useDispatch } from 'react-redux';
import { FolderStructure } from '../../api-client/autogenerated';
import { addSnackbar } from '../../features/snackbar/actions';
import {
  deleteSubscriberFolderStructure,
  getSubscriberFolderStructures,
  insertSubscriberFolderStructure,
  updateSubscriberFolderStructure,
} from '../../models/api/subscribers';
import { ascendingComparator } from '../document-index/DocumentIndexUtils';
import CircularLoader from '../loader/CircularLoader';
import PageTitle from '../page-title/PageTitle';
import DashboardBlock from './DashboardBlock';
import FolderStructureEditor, { FolderStructureDraft } from './FolderStructureEditor';
import FolderStructureTableRow from './FolderStructureTableRow';

const useStyles = makeStyles(() =>
  createStyles({
    content: {
      display: 'flex',
      flexDirection: 'column',
    },
    table: {
      border: '1px solid rgba(201, 200, 201, .8)',
    },
  }),
);

type FolderStructuresProps = {
  subscriberId: string;
};

/**
 * Lets a subscriber admin keep several named default CENTERLINE Drive folder structures. One of
 * them can be picked when a project is created, which is the only time they are applied -- projects
 * that already exist are never touched.
 */
export default function FolderStructures(props: FolderStructuresProps) {
  const { subscriberId } = props;
  const classes = useStyles();
  const dispatch = useDispatch();

  const [structures, setStructures] = useState<FolderStructure[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [willCreate, setWillCreate] = useState(false);
  const [selectedStructure, setSelectedStructure] = useState<FolderStructure | null>(null);
  /** The structure a new one is being cloned from, which also names the editor. */
  const [cloneSource, setCloneSource] = useState<FolderStructure | undefined>();

  const refresh = () => getSubscriberFolderStructures(subscriberId).then(setStructures);

  useEffect(() => {
    if (!subscriberId) return;

    let isCurrent = true;
    setIsLoading(true);
    getSubscriberFolderStructures(subscriberId)
      .then((result) => {
        if (isCurrent) setStructures(result);
      })
      .finally(() => {
        if (isCurrent) setIsLoading(false);
      });
    // eslint-disable-next-line consistent-return
    return () => {
      isCurrent = false;
    };
  }, [subscriberId]);

  const closeEditor = () => {
    setWillCreate(false);
    setSelectedStructure(null);
    setCloneSource(undefined);
  };

  const handleSave = async (draft: FolderStructureDraft) => {
    if (selectedStructure) {
      await updateSubscriberFolderStructure(subscriberId, selectedStructure.id, draft);
    } else {
      await insertSubscriberFolderStructure(subscriberId, draft);
    }
    await refresh();
    closeEditor();
    dispatch(
      addSnackbar({
        id: Date.now(),
        message: `${draft.name} saved!`,
        severity: 'success',
      }),
    );
  };

  const handleDelete = async (structure: FolderStructure) => {
    const proceed = window.confirm(
      `Are you sure you want to delete the folder structure: ${structure.name}?`,
    );
    if (!proceed) return;

    await deleteSubscriberFolderStructure(subscriberId, structure.id);
    if (selectedStructure?.id === structure.id) closeEditor();
    await refresh();
    dispatch(
      addSnackbar({
        id: Date.now(),
        message: `${structure.name} successfully deleted!`,
        severity: 'success',
      }),
    );
  };

  const getContent = () => {
    if (isLoading) return <CircularLoader />;

    return (
      <>
        <Typography variant="body1" style={{ marginBottom: 16 }}>
          Pick one of these when creating a project to have its folders created in that
          project&apos;s CENTERLINE Drive. Existing projects are not changed.
        </Typography>
        <Table className={classes.table}>
          <TableHead>
            <TableRow>
              <TableCell style={{ width: '180px' }} />
              <TableCell style={{ width: '250px' }}>Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell style={{ width: '150px' }}># of Folders</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {structures.length === 0 && (
              <TableRow>
                <TableCell colSpan={4}>No existing folder structures</TableCell>
              </TableRow>
            )}
            {structures
              .slice()
              .sort((a, b) => ascendingComparator(a, b, 'name'))
              .map((structure, index) => (
                <FolderStructureTableRow
                  key={structure.id}
                  structure={structure}
                  index={index}
                  editOnClick={(next) => {
                    setWillCreate(false);
                    setCloneSource(undefined);
                    setSelectedStructure(next);
                  }}
                  deleteOnClick={handleDelete}
                />
              ))}
          </TableBody>
        </Table>
        <div style={{ marginTop: 16 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              setSelectedStructure(null);
              setCloneSource(undefined);
              setWillCreate(true);
            }}
          >
            Create New Structure
          </Button>
        </div>
        {selectedStructure || willCreate ? (
          <div style={{ marginTop: 32 }}>
            <PageTitle
              title={
                // eslint-disable-next-line no-nested-ternary
                selectedStructure
                  ? `Editing ${selectedStructure.name}`
                  : cloneSource
                  ? `Cloning ${cloneSource.name}`
                  : 'Create New Structure'
              }
            />
            <FolderStructureEditor
              // Remount on switching rows so the editor picks up the newly selected structure.
              key={selectedStructure?.id || 'new'}
              existingStructure={selectedStructure || undefined}
              cloneOptions={structures}
              onCloneSourceChange={setCloneSource}
              onSave={handleSave}
              onCancel={closeEditor}
            />
          </div>
        ) : null}
      </>
    );
  };

  return (
    <DashboardBlock title="CENTERLINE Drive Default Folder Structures">
      <CardContent className={classes.content}>{getContent()}</CardContent>
    </DashboardBlock>
  );
}
